import * as React from 'react';
import { RouteComponentProps, useLocation } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation, useQuery } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';

import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';
import { createGTMEvent } from '../../../../utils/tracking/gtm';
import { BaseHeroContent, Hero } from '../../../ui/hero/Index';
import { BROKERS_ON_OFFICE_QUERY } from '../../../../../client/__graphql__/queries/getBrokersOnOffice';
import {
  GetBrokersOnOffice,
  GetBrokersOnOfficeVariables
} from '../../../../__types__/generated/GetBrokersOnOffice';
import { BrokerItem } from '../../../ui/broker/Item';
import { OFFICE_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/officeValuation';
import {
  OfficeValuation,
  OfficeValuationVariables
} from '../../../../__types__/generated/OfficeValuation';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

interface IRoute {
  office: string;
  path: string;
}

const DinLokaleMegler: React.FC = () => {
  const location = useLocation();
  const { routeConfig } = React.useContext(SiteContext);

  const { submitWithDigger } = useDigtectiveContext();

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const { data } = useQuery<GetBrokersOnOffice, GetBrokersOnOfficeVariables>(
    BROKERS_ON_OFFICE_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: {
        input: {
          urltag: 'vikoyrbrandt'
        }
      }
    }
  );

  const [lead, { error }] = useMutation<
    OfficeValuation,
    OfficeValuationVariables
  >(OFFICE_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      setLoading(false);
      if (lead.valuation?.success) {
        window.scroll(0, 0);
        setPosted(true);

        // GTM tracking

        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.KONTOR,
              identifier: 'vikoyrbrandt'
            }
          }
        });
      });
    }
  });

  return (
    <>
      <Seo
        title="Din lokale megler - PrivatMegleren Vikøyr & Brandt"
        description="Vi har salgsgaranti. Selger du ikke, betaler du ikke."
        url={location.pathname}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/content/image_vikoyr.jpeg"
        opacity={0.8}
        height="600px"
        className="top-header-margin"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            Din lokale megler <br />- Vikøyr & Brandt
          </Heading>
          <Paragraph center={true}>
            Vi har salgsgaranti. Selger du ikke, betaler du ikke.
          </Paragraph>
        </HeroContent>
      </Hero>
      <Container className="padding-top" style={pageFadeIn}>
        <BrokerList style={pageFadeIn}>
          {data?.getOffice?.brokers?.list &&
            data.getOffice.brokers.list.map((item) => {
              return (
                <BrokerItem
                  key={`broker_${item.name}`}
                  name={item.name}
                  title={item.title}
                  phone={item.cellphone}
                  image={item.image.full}
                  office={null}
                  showImage={true}
                  path={item.path}
                />
              );
            })}
        </BrokerList>
        <FormWrapper style={fadeOut}>
          <Section className="form-section" style={{ paddingBottom: '10px' }}>
            <Heading tag="h2" center={true}>
              Legg inn info for å bli kontaktet
            </Heading>
          </Section>
          <Form onSubmit={handleSubmit} noValidate>
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item: any, index: number) => {
              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Send
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
          </Section>
        </Thanks>
      </Container>
    </>
  );
};

export default DinLokaleMegler;

const FormWrapper = styled(animated.div)``;

const HeroContent = styled(animated.div)`
  ${BaseHeroContent}
  flex-flow: column;
  max-width: 960px;
`;

const BrokerList = styled(animated.div)`
  display: grid;
  justify-items: center;
  grid-template-columns: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 20px;
  width: 100%;
  padding-bottom: 30px;

  @media all and (min-width: 550px) {
    grid-template-columns: 1fr 1fr;
  }
  @media all and (min-width: 650px) and (max-width: 850px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media all and (min-width: 850px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`;
